import Backbone from 'backbone'
import template from './configurable-list.hbs'
import configurableListItemActions from './configurable-list-item-actions.hbs'
import formHelper from 'scoreboard/components/form-helper'
import _ from 'lodash'
import DraggableList from 'draggable-list'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'

const ConfigurableList = Backbone.View.extend(/** @lends {ConfigurableList} */{

	className: 'configurable-list',

	events: {
		'click > .add-list-item': 'onClickAddListItem',
		'click .delete-list-item': 'onClickDeleteListItem',
		'mouseenter li.item': 'addDragItemHoverClass',
		'mouseleave li.item': 'removeDragItemHoverClass'
	},

	/**
	 * A list of items that can be added, edited, removed, and reordered.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {String|Function} [options.addLabel='common.add']
	 *
	 * @param {Function} options.itemViewCreator a function which returns a
	 * view which renders a single item in the list
	 *
	 * @param {Array} [options.itemData] the data used to populate each item
	 * template
	 *
	 * @param {Boolean} [options.allowLastItemDeletion=false] whether to allow
	 * deletion of the last item in the list
	 *
	 * @param {Boolean} [options.nested=false] whether the list is nested inside
	 * another draggable list. In that case, we have to wait to render the
	 * draggable list for this list until after the outer draggable list has
	 * been rendered
	 */
	initialize: function(options = { nested: false }) {
		this.options = options
		this.addLabel = options.addLabel || 'common.add'
		this.allowLastItemDeletion = options.allowLastItemDeletion ?? false
	},

	render: function() {
		this.$el.html(template(this.context()))
		// must be before this.renderItems and this.renderDraggableList
		this.draggableListEl = this.$('> .list-container > .draggable-list')
		this.renderItems()
		if (!this.options.nested) {
			this.finishRendering()
		}
		this.toggle()
		return this
	},

	finishRendering: function() {
		this.renderDraggableList()
	},

	context: function() {
		return {
			addLabel: _.isFunction(this.addLabel) ? this.addLabel() : this.addLabel,
			addButtonExtraClass: this.options.addButtonExtraClass,
			customButton: this.options.customButton
		}
	},

	renderItems: function() {
		this.options.itemData?.forEach((itemDatum, index) => {
			this.addItem(itemDatum, index)
		})
		this.toggleEmptyState()
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
	},

	addItem: function(itemDatum, index) {
		let view = this.options.itemViewCreator?.(itemDatum, index)

		this.draggableListEl.append('<li class="item"></li>')

		let itemEl = this.draggableListEl.find('.item').last()
		view && itemEl.append(view.el)
		itemEl.append(configurableListItemActions())
		itemEl.data('view', view)

		view && view.render()
	},

	renderDraggableList: function() {
		this.draggableListEl.find('.item').removeClass('draggable-list-nodrag')
		this.draggableList = new DraggableList(this.draggableListEl[0])
		// #52732 since we're constantly re-creating the list, fire an event
		// from the configurable list so that dependencies of the
		// configurable-list don't need to know about this complexity. they can
		// just listen to the move event
		this.draggableList.on('move', () => this.trigger('move'))
		this.draggableList.on('dndstop', () => this.trigger('dndstop'))
	},

	onClickAddListItem: function(e) {
		this.addItem()
		this.toggle()
		this.renderDraggableList()
		// set focus in the first input element of the new item
		this.draggableListEl.find('> .item:last-child')
			.find('input')
			.eq(0)
			.focus()
		this.trigger('add-list-item', e)
		this.toggleEmptyState()
	},

	onClickDeleteListItem: function(e) {
		const itemEl = this.$(e.target).closest('.item')
		const view = itemEl.data('view')
		itemEl.remove()
		this.toggle()
		this.renderDraggableList()
		this.trigger('delete-list-item', e, { view })
		this.toggleEmptyState()
	},

	// We only want a hover state on the item we are hovering, but in CSS,
	// we can't tell the parent to change its style based on the child's
	// hover state so we need to add a class we can target.
	addDragItemHoverClass: function(e) {
		this.removeDragItemHoverClass()
		let target = this.$(e.currentTarget)
		target.parents('li.item').addClass('is-hovering-child')
	},

	removeDragItemHoverClass: function() {
		this.$('.is-hovering-child').removeClass('is-hovering-child')
	},

	toggleEmptyState: function() {
		let isEmpty = this.$('.list').children().length === 0
		this.$el.toggleClass('is-empty', isEmpty)
	},

	/**
	 * Toggles the visibility of controls in the list based on the list's
	 * current state
	 */
	toggle: function() {
		if (!this.allowLastItemDeletion) {
			let listItemActions = this.draggableListEl.find('> .item > .list-item-actions')
			let numItems = listItemActions.length
			let showDeleteButtons = numItems > 1
			listItemActions.find('> .delete-list-item')
				.toggleClass('is-not-displayed', !showDeleteButtons)
		}
	},

	readFromDom: function() {
		return _.map(this.draggableListEl.find('.item'), formHelper.readFromDom)
	},

	autoRemove: true

})

/**
 *
 * @param items {{key: string, value: string}[]}
 * @returns {{key: string, value: string}[]}
 */
function filterEmptyItems(items) {
	return _(items)
		.reject(_.isNil)
		.reject(_.isEmpty)
		.reject(['key', _.isNil])
		.reject(['key', _.isEmpty])
		.reject(['value', _.isNil])
		.reject(['value', _.isEmpty]).value()
}

export default ConfigurableList
export { filterEmptyItems }

ConfigurableList.__filename__ = 'components/configurable-list.js'
